import dayjs from "dayjs";
import pluralize from "pluralize";

export enum OffsetDurationUnit {
    DAYS = "day",
    HOURS = "hour",
    MINUTES = "minute",
}

export interface Duration {
    duration: number | null;
    durationUnit: OffsetDurationUnit;
}

export const convertToMinutes = (duration: number, fromUnit: OffsetDurationUnit) => {
    switch (fromUnit) {
        case OffsetDurationUnit.HOURS:
            return duration * 60;
        case OffsetDurationUnit.DAYS:
            return duration * 24 * 60;
        default:
            return duration;
    }
};

export const convertMinutesToMs = (minutes: number) => minutes * 60 * 1000;

export const humanizeDuration = (duration: number) => {
    const hour = 60;
    const day = hour * 24;
    let unit;

    if (duration >= day && duration % day === 0) {
        // Offset should be displayed in days
        duration /= day;
        unit = OffsetDurationUnit.DAYS;
    } else {
        // Offset should be displayed in hours
        duration /= hour;
        unit = OffsetDurationUnit.HOURS;
    }

    return {
        duration,
        durationUnit: unit,
    };
};

export const humanizeEquipmentDuration = (duration: number) => {
    const hour = 60;
    if (duration < hour || duration % hour !== 0) {
        // no decimal values allowed, so duration must be in minutes
        return {
            duration: duration,
            durationUnit: OffsetDurationUnit.MINUTES,
        };
    }

    return humanizeDuration(duration);
};

const MINUTES_IN_HOUR = 60;
const MINUTES_IN_DAY = 24 * MINUTES_IN_HOUR;

/**
 * There is an issue with `dayjs.duration(value, "minutes")` when the `value` is bigger than (30 days in minutes).
 * The Duration object contains 14 hours and not complete round-off to the days. To fix that, we are using the following function
 *
 * Example, when we select 35 days (50400 minutes), the output duration object contains (month: 1, days: 4, hours: 14). Which is not correct
 */
export const getDuration = (value: number) => {
    if (value % MINUTES_IN_DAY === 0) {
        return dayjs.duration({ days: Math.round(value / MINUTES_IN_DAY) });
    }

    if (value % MINUTES_IN_HOUR === 0) {
        return dayjs.duration({ hours: Math.round(value / MINUTES_IN_HOUR) });
    }

    return dayjs.duration(value, "minutes");
};

export const formatDuration = (minutes: number) => {
    if (!minutes) return "";

    const { duration, durationUnit } = humanizeEquipmentDuration(minutes);
    return `${duration} ${pluralize(durationUnit, duration)}`;
};
